import React, { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { authenticatedFetch, getAuthToken, getBackendUrl } from '../utils/auth';
import './CameraViewer.css';

const API_BASE_URL = getBackendUrl();

const CameraViewer = ({ camera, onBack, onAuthError }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('Initializing...');
  const [hlsStats, setHlsStats] = useState({
    level: 0,
    buffered: 0,
    currentTime: 0,
    duration: 0
  });
  const [viewMode, setViewMode] = useState('live'); // 'live' or 'history'
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasVideoContent, setHasVideoContent] = useState(false); // Track if video has content
  const [motionEvents, setMotionEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [eventsError, setEventsError] = useState(null);
  const [actualVideoStartTime, setActualVideoStartTime] = useState(null); // Track actual video start time
  const [isLoadingLive, setIsLoadingLive] = useState(false); // Track live stream loading state
  
  // Store raw input values to avoid padding issues
  const [startTimeInputs, setStartTimeInputs] = useState({ hours: '00', minutes: '00', seconds: '00' });
  const [endTimeInputs, setEndTimeInputs] = useState({ hours: '00', minutes: '00', seconds: '00' });

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const isInitializedRef = useRef(false);
  const videoEventListenersRef = useRef(null);

  // Get camera ID from props or fallback
  const cameraId = camera?.camera_id || 'camera_01';

  // Add debug logging
  const addDebugLog = (message) => {
    console.log(`[DEBUG] ${message}`);
    setDebugInfo(prev => `${new Date().toLocaleTimeString()}: ${message}`);
  };

  // Get current date/time for default values
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Initialize default date/time values
  useEffect(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
    
    // Start time: today at 00:00:00
    const startTime = today.getFullYear() + '-' + 
                     String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                     String(today.getDate()).padStart(2, '0') + 'T00:00:00';
    
    // End time: today at 23:59:59
    const endTime = today.getFullYear() + '-' + 
                   String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                   String(today.getDate()).padStart(2, '0') + 'T23:59:59';
    
    setEndDateTime(endTime);
    setStartDateTime(startTime);
    
    // Update time input fields to match the actual datetime values
    setStartTimeInputs({
      hours: '00',
      minutes: '00',
      seconds: '00'
    });
    
    setEndTimeInputs({
      hours: '23',
      minutes: '59',
      seconds: '59'
    });
  }, []);

  // Check HLS.js support
  const checkHlsSupport = () => {
    if (Hls.isSupported()) {
      addDebugLog('✅ HLS.js is supported');
      return true;
    } else if (videoRef.current?.canPlayType('application/vnd.apple.mpegurl')) {
      addDebugLog('✅ Native HLS support detected (Safari)');
      return true;
    } else {
      addDebugLog('❌ HLS not supported');
      return false;
    }
  };

  // Initialize HLS player
  const initializeHls = (playlistUrl) => {
    if (!checkHlsSupport()) {
      setError('HLS not supported in this browser');
      return;
    }

    if (!videoRef.current) {
      addDebugLog('Video element not ready for HLS initialization');
      return;
    }

    // Clean up existing HLS instance if any
    if (hlsRef.current) {
      addDebugLog('Cleaning up existing HLS instance');
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    addDebugLog('Initializing HLS.js...');
    
    // Configure HLS based on mode
    const hlsConfig = {
      debug: false,
      enableWorker: true,
      backBufferLength: 60,
      maxBufferLength: 120,
      maxMaxBufferLength: 180,
    };

    // Add live-specific settings only for live mode
    if (viewMode === 'live') {
      hlsConfig.lowLatencyMode = true;
      hlsConfig.liveSyncDurationCount = 3;
      hlsConfig.liveMaxLatencyDurationCount = 5;
    } else {
      // History mode - disable live features
      hlsConfig.lowLatencyMode = false;
    }

    const hls = new Hls(hlsConfig);

    hlsRef.current = hls;

    // HLS events
    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      addDebugLog('HLS media attached');
      isInitializedRef.current = true;
      
      addDebugLog(`Loading playlist: ${playlistUrl}`);
      hls.loadSource(playlistUrl);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
      addDebugLog(`HLS manifest parsed: ${data.levels.length} levels`);
      setHasVideoContent(true); // Video content is now available
      
      // Start playing
      videoRef.current.play().catch(err => {
        addDebugLog(`Auto-play failed: ${err.message}`);
      });
    });

    hls.on(Hls.Events.MANIFEST_LOADED, (event, data) => {
      addDebugLog(`Manifest loaded: ${data.details.fragments.length} fragments`);
    });

    hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
      addDebugLog(`Level loaded: ${data.details.fragments.length} fragments, live: ${data.details.live}`);
      
      // For history mode, try to determine the actual video start time from the first fragment
      if (viewMode === 'history' && data.details.fragments.length > 0) {
        const firstFragment = data.details.fragments[0];
        const lastFragment = data.details.fragments[data.details.fragments.length - 1];
        
        addDebugLog(`First fragment: ${firstFragment.url.split('/').pop()}`);
        addDebugLog(`Last fragment: ${lastFragment.url.split('/').pop()}`);
        
        if (firstFragment && firstFragment.programDateTime) {
          const actualStartTime = new Date(firstFragment.programDateTime);
          setActualVideoStartTime(actualStartTime);
          addDebugLog(`Detected actual video start time from programDateTime: ${actualStartTime.toISOString()}`);
        } else if (firstFragment && firstFragment.url) {
          // Try to extract timestamp from fragment URL
          // Pattern: CAMERA_ID_YYYYMMDD_HHMMSS_XX.ts
          const urlMatch = firstFragment.url.match(/(\d{8})_(\d{6})/);
          if (urlMatch) {
            const dateStr = urlMatch[1]; // YYYYMMDD
            const timeStr = urlMatch[2]; // HHMMSS
            
            const year = dateStr.substring(0, 4);
            const month = dateStr.substring(4, 6);
            const day = dateStr.substring(6, 8);
            const hour = timeStr.substring(0, 2);
            const minute = timeStr.substring(2, 4);
            const second = timeStr.substring(4, 6);
            
            const actualStartTime = new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}`);
            setActualVideoStartTime(actualStartTime);
            addDebugLog(`Extracted actual video start time from fragment URL: ${actualStartTime.toISOString()}`);
            addDebugLog(`Parsed from first fragment: ${firstFragment.url.split('/').pop()}`);
          } else {
            addDebugLog(`Could not extract timestamp from fragment URL: ${firstFragment.url}`);
          }
        }
      }
      
      if (data.details.fragments.length > 0) {
        const latestFrag = data.details.fragments[data.details.fragments.length - 1];
        addDebugLog(`Latest fragment: ${latestFrag.url.split('/').pop()}`);
      }
    });

    hls.on(Hls.Events.ERROR, (event, data) => {
      addDebugLog(`HLS error: ${data.type} - ${data.details}`);
      
      if (data.fatal) {
        // Set loading to false for live mode on fatal errors
        if (viewMode === 'live') {
          setIsLoadingLive(false);
        }
        
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            addDebugLog('Fatal network error, attempting recovery');
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            addDebugLog('Fatal media error, attempting recovery');
            hls.recoverMediaError();
            break;
          default:
            addDebugLog('Fatal error, cannot recover');
            setError(`HLS fatal error: ${data.details}`);
            break;
        }
      }
    });

    // Attach to video element
    hls.attachMedia(videoRef.current);
    
    // Add video event listeners for live mode
    if (viewMode === 'live') {
      const handleVideoPlaying = () => {
        addDebugLog('Video started playing - hiding loading indicator');
        setIsLoadingLive(false);
        // Clean up listeners after use
        if (videoEventListenersRef.current) {
          videoRef.current.removeEventListener('playing', videoEventListenersRef.current.handleVideoPlaying);
          videoRef.current.removeEventListener('loadeddata', videoEventListenersRef.current.handleVideoLoadedData);
          videoEventListenersRef.current = null;
        }
      };
      
      const handleVideoLoadedData = () => {
        addDebugLog('Video loaded data - checking if playing');
        // Only hide loading if video is actually playing
        if (!videoRef.current.paused) {
          setIsLoadingLive(false);
          // Clean up listeners after use
          if (videoEventListenersRef.current) {
            videoRef.current.removeEventListener('playing', videoEventListenersRef.current.handleVideoPlaying);
            videoRef.current.removeEventListener('loadeddata', videoEventListenersRef.current.handleVideoLoadedData);
            videoEventListenersRef.current = null;
          }
        }
      };
      
      // Store listener references for cleanup
      videoEventListenersRef.current = {
        handleVideoPlaying,
        handleVideoLoadedData
      };
      
      videoRef.current.addEventListener('playing', handleVideoPlaying);
      videoRef.current.addEventListener('loadeddata', handleVideoLoadedData);
    }
  };

  // Load live stream
  const loadLiveStream = () => {
    setIsLoadingLive(true);
    const playlistUrl = `${API_BASE_URL}/get_live_playlist/${cameraId}.m3u8?token=${getAuthToken()}`;
    initializeHls(playlistUrl);
  };

  // Load historical stream
  const loadHistoryStream = async () => {
    if (!startDateTime || !endDateTime) {
      setError('Please select both start and end date/time');
      return;
    }

    setIsLoadingHistory(true);
    setError(null);
    setActualVideoStartTime(null); // Reset actual video start time for new history load

    try {
      // Format datetime as YYYY-MM-DDTHH:MM:SS without timezone
      const formatDateTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const startFormatted = formatDateTime(startDateTime);
      const endFormatted = formatDateTime(endDateTime);
      
      const playlistUrl = `${API_BASE_URL}/get_history_playlist/${cameraId}.m3u8?start_date_time=${startFormatted}&end_date_time=${endFormatted}&token=${getAuthToken()}`;
      addDebugLog(`Loading history playlist: ${playlistUrl}`);
      
      // Test if the playlist exists
      const response = await authenticatedFetch(playlistUrl);
      if (!response.ok) {
        throw new Error(`Failed to load history: ${response.status} ${response.statusText}`);
      }
      
      initializeHls(playlistUrl);
      
      // Also fetch motion events
      await fetchMotionEvents();
    } catch (err) {
      addDebugLog(`History load error: ${err.message}`);
      if (err.message === 'Authentication failed' || err.message === 'Not authenticated') {
        // Handle authentication error
        if (onAuthError) {
          onAuthError();
        }
        return;
      }
      setError(`Failed to load history: ${err.message}`);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Handle time input key press with proper text replacement
  const handleTimeKeyPress = (e, type, field, nextFieldRef) => {
    const key = e.key;
    
    // Allow backspace, delete, tab, arrow keys
    if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(key)) {
      return;
    }
    
    // Only allow digits
    if (!/^\d$/.test(key)) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    
    const input = e.target;
    const currentValue = input.value;
    const cursorPosition = input.selectionStart;
    
    let newValue;
    
    // If text is selected or cursor at start, replace entire value
    if (input.selectionStart !== input.selectionEnd || cursorPosition === 0) {
      newValue = key;
    } else if (currentValue.length < 2) {
      // If less than 2 digits, append
      newValue = currentValue + key;
    } else {
      // If 2 digits already, replace from cursor position
      newValue = currentValue.substring(0, cursorPosition) + key + currentValue.substring(cursorPosition + 1);
    }
    
    // Validate the new value
    const numValue = parseInt(newValue);
    let isValid = false;
    
    if (field === 'hours') {
      isValid = numValue >= 0 && numValue <= 23;
    } else {
      isValid = numValue >= 0 && numValue <= 59;
    }
    
    if (!isValid && newValue.length === 2) {
      return;
    }
    
    // Update the raw input values
    if (type === 'start') {
      const newInputs = { ...startTimeInputs, [field]: newValue };
      setStartTimeInputs(newInputs);
      
      // Update formatted datetime
      const date = startDateTime.split('T')[0];
      const newTime = `${newInputs.hours.padStart(2, '0')}:${newInputs.minutes.padStart(2, '0')}:${newInputs.seconds.padStart(2, '0')}`;
      setStartDateTime(`${date}T${newTime}`);
    } else {
      const newInputs = { ...endTimeInputs, [field]: newValue };
      setEndTimeInputs(newInputs);
      
      // Update formatted datetime
      const date = endDateTime.split('T')[0];
      const newTime = `${newInputs.hours.padStart(2, '0')}:${newInputs.minutes.padStart(2, '0')}:${newInputs.seconds.padStart(2, '0')}`;
      setEndDateTime(`${date}T${newTime}`);
    }
    
    // Auto-advance to next field when 2 digits are entered
    if (newValue.length === 2 && nextFieldRef?.current) {
      setTimeout(() => {
        nextFieldRef.current.focus();
        nextFieldRef.current.select();
      }, 0);
    }
  };

  // Handle focus to select all text
  const handleTimeFocus = (e) => {
    e.target.select();
  };

  // Refs for auto-focus progression
  const startMinuteRef = useRef(null);
  const startSecondRef = useRef(null);
  const endMinuteRef = useRef(null);
  const endSecondRef = useRef(null);

  // Handle mode change
  const handleModeChange = (mode) => {
    setViewMode(mode);
    setError(null);
    setHasVideoContent(false); // Reset video content state
    setActualVideoStartTime(null); // Reset actual video start time
    setIsLoadingLive(false); // Reset loading state
    
    // Clear motion events when switching away from history mode
    if (mode !== 'history') {
      setMotionEvents([]);
      setEventsError(null);
    }
    
    // Clean up existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
      isInitializedRef.current = false;
    }
    
    // Clean up video event listeners
    if (videoRef.current && videoEventListenersRef.current) {
      videoRef.current.removeEventListener('playing', videoEventListenersRef.current.handleVideoPlaying);
      videoRef.current.removeEventListener('loadeddata', videoEventListenersRef.current.handleVideoLoadedData);
      videoEventListenersRef.current = null;
    }

    // Reset video element to clear any stuck states
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute('src');
      videoRef.current.load(); // This resets the video element
      addDebugLog(`Video element reset for ${mode} mode`);
    }

    // Only auto-load live stream if switching to live mode
    // The useEffect will handle the initialization
    if (mode === 'live') {
      // Reset initialization flag to allow re-initialization
      isInitializedRef.current = false;
    }
  };

  // Handle history load
  const handleLoadHistory = () => {
    loadHistoryStream();
  };

  // Update HLS statistics
  const updateHlsStats = () => {
    if (videoRef.current && hlsRef.current) {
      const video = videoRef.current;
      const buffered = video.buffered;
      const bufferedEnd = buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;
      
      setHlsStats({
        level: hlsRef.current.currentLevel,
        buffered: bufferedEnd,
        currentTime: video.currentTime,
        duration: video.duration || 0
      });
    }
  };

  // Fetch motion detection events
  const fetchMotionEvents = async () => {
    if (!startDateTime || !endDateTime) {
      return;
    }

    setIsLoadingEvents(true);
    setEventsError(null);

    try {
      // Format datetime as YYYY-MM-DDTHH:MM:SS without timezone
      const formatDateTime = (dateTimeString) => {
        const date = new Date(dateTimeString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      };

      const startFormatted = formatDateTime(startDateTime);
      const endFormatted = formatDateTime(endDateTime);
      
      const eventsUrl = `${API_BASE_URL}/get_motion_events/${cameraId}?start_date_time=${startFormatted}&end_date_time=${endFormatted}&token=${getAuthToken()}`;
      addDebugLog(`Fetching motion events: ${eventsUrl}`);
      
      const response = await authenticatedFetch(eventsUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch motion events: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setMotionEvents(data.merged_events || []);
      addDebugLog(`Loaded ${data.merged_events?.length || 0} motion events`);
    } catch (err) {
      addDebugLog(`Motion events fetch error: ${err.message}`);
      if (err.message === 'Authentication failed' || err.message === 'Not authenticated') {
        // Handle authentication error
        if (onAuthError) {
          onAuthError();
        }
        return;
      }
      setEventsError(`Failed to load motion events: ${err.message}`);
      setMotionEvents([]);
    } finally {
      setIsLoadingEvents(false);
    }
  };

  // Handle motion event click - seek to event start time
  const handleEventClick = (event) => {
    if (!videoRef.current || !hlsRef.current) {
      addDebugLog('Video or HLS not ready for seeking');
      return;
    }

    try {
      // Parse the event start time
      const eventStartTime = new Date(event.start_time);
      
      // Use actual video start time if available, otherwise fall back to requested start time
      const videoStartTime = actualVideoStartTime || new Date(startDateTime);
      
      // Calculate the offset in seconds from the actual start of the video
      const offsetSeconds = (eventStartTime - videoStartTime) / 1000;
      
      addDebugLog(`Seeking to motion event at ${event.start_time}`);
      addDebugLog(`Requested history start: ${startDateTime}`);
      addDebugLog(`Actual video start: ${videoStartTime.toISOString()}`);
      addDebugLog(`Event offset from actual video start: ${offsetSeconds}s`);
      addDebugLog(`Video duration: ${videoRef.current.duration}s`);
      
      // Check if the event is within the video timeline
      if (offsetSeconds < 0) {
        addDebugLog(`Event is before video start time, seeking to beginning`);
        videoRef.current.currentTime = 0;
        return;
      }
      
      // Ensure the offset is within the video duration
      const seekPosition = Math.min(offsetSeconds, videoRef.current.duration || offsetSeconds);
      
      addDebugLog(`Seeking to position: ${seekPosition}s`);
      
      // Seek to the calculated position
      videoRef.current.currentTime = seekPosition;
    } catch (err) {
      addDebugLog(`Error seeking to event: ${err.message}`);
    }
  };

  // Format time for display
  const formatEventTime = (timeString) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString();
    } catch (err) {
      return timeString;
    }
  };

  // Calculate event duration
  const getEventDuration = (startTime, endTime) => {
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);
      const durationMs = end - start;
      const durationSeconds = Math.round(durationMs / 1000);
      return `${durationSeconds}s`;
    } catch (err) {
      return 'N/A';
    }
  };

  // Initialize on component mount
  useEffect(() => {
    addDebugLog('Component initializing...');
    setLoading(false);
    
    // Set loading state for live mode on initial mount
    if (viewMode === 'live') {
      setIsLoadingLive(true);
    }

    return () => {
      addDebugLog('Component cleanup...');
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      
      // Clean up video event listeners
      if (videoRef.current && videoEventListenersRef.current) {
        videoRef.current.removeEventListener('playing', videoEventListenersRef.current.handleVideoPlaying);
        videoRef.current.removeEventListener('loadeddata', videoEventListenersRef.current.handleVideoLoadedData);
        videoEventListenersRef.current = null;
      }
      
      isInitializedRef.current = false;
    };
  }, []);

  // Initialize HLS when video element is ready
  useEffect(() => {
    let interval = null;
    let timeout = null;

    const checkVideoReady = () => {
      if (videoRef.current && !isInitializedRef.current && !hlsRef.current && viewMode === 'live') {
        addDebugLog('Video element ready, initializing live HLS...');
        loadLiveStream();
        return true;
      }
      return false;
    };

    // Only auto-initialize for live mode
    if (viewMode === 'live') {
      if (!checkVideoReady()) {
        addDebugLog('Video element not ready, checking periodically...');
        interval = setInterval(() => {
          if (checkVideoReady()) {
            clearInterval(interval);
            interval = null;
          }
        }, 100);

        timeout = setTimeout(() => {
          if (interval) {
            clearInterval(interval);
            interval = null;
          }
          // Only set error if we're still in live mode and haven't initialized
          if (!isInitializedRef.current && !hlsRef.current && viewMode === 'live') {
            addDebugLog('Timeout waiting for video element');
            setError('Video element not available');
          }
        }, 5000);
      }
    } else {
      // For history mode, just log that we're waiting for user input
      addDebugLog('History mode - waiting for user to select date/time and load history');
    }

    // Cleanup function
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [viewMode]);

  // Update stats periodically
  useEffect(() => {
    const interval = setInterval(updateHlsStats, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="camera-viewer">
        <div className="loading">Loading camera viewer...</div>
        <div className="debug-info">{debugInfo}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="camera-viewer">
        <div className="camera-header">
          <button onClick={onBack} className="back-button">
            ← Back to Cameras
          </button>
          <h2>{camera?.name || 'Camera Viewer'}</h2>
        </div>
        <div className="error">Error: {error}</div>
        <div className="error-details">
          Make sure your backend API is running on {API_BASE_URL.replace('/api', '')} and the requested data is available.
        </div>
        <div className="debug-info">{debugInfo}</div>
      </div>
    );
  }

  return (
    <div className="camera-viewer">
      <div className="camera-header">
        <button onClick={onBack} className="back-button">
          ← Back to Cameras
        </button>
        <h2>{camera?.name || 'Camera Viewer'}</h2>
        <div className="mode-buttons">
          <button 
            className={`mode-button ${viewMode === 'live' ? 'active' : ''}`}
            onClick={() => handleModeChange('live')}
            disabled={isLoadingLive || (viewMode === 'live' && !error)}
          >
            {isLoadingLive ? 'Loading...' : 'Live View'}
          </button>
          <button 
            className={`mode-button ${viewMode === 'history' ? 'active' : ''}`}
            onClick={() => handleModeChange('history')}
          >
            History View
          </button>
        </div>
      </div>

      {viewMode === 'history' && (
        <div className="view-mode-controls">
          <div className="history-controls">
            <div className="datetime-inputs">
              <div className="datetime-group">
                <label>Start Date/Time:</label>
                <div className="datetime-split">
                  <input
                    type="date"
                    value={startDateTime.split('T')[0]}
                    onChange={(e) => {
                      const time = startDateTime.split('T')[1] || '00:00:00';
                      setStartDateTime(`${e.target.value}T${time}`);
                    }}
                  />
                  <div className="time-inputs">
                    <input
                      type="text"
                      maxLength="2"
                      placeholder="HH"
                      value={startTimeInputs.hours}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'start', 'hours', startMinuteRef)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                    <span className="time-separator">:</span>
                    <input
                      ref={startMinuteRef}
                      type="text"
                      maxLength="2"
                      placeholder="MM"
                      value={startTimeInputs.minutes}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'start', 'minutes', startSecondRef)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                    <span className="time-separator">:</span>
                    <input
                      ref={startSecondRef}
                      type="text"
                      maxLength="2"
                      placeholder="SS"
                      value={startTimeInputs.seconds}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'start', 'seconds', null)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="datetime-group">
                <label>End Date/Time:</label>
                <div className="datetime-split">
                  <input
                    type="date"
                    value={endDateTime.split('T')[0]}
                    onChange={(e) => {
                      const time = endDateTime.split('T')[1] || '00:00:00';
                      setEndDateTime(`${e.target.value}T${time}`);
                    }}
                  />
                  <div className="time-inputs">
                    <input
                      type="text"
                      maxLength="2"
                      placeholder="HH"
                      value={endTimeInputs.hours}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'end', 'hours', endMinuteRef)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                    <span className="time-separator">:</span>
                    <input
                      ref={endMinuteRef}
                      type="text"
                      maxLength="2"
                      placeholder="MM"
                      value={endTimeInputs.minutes}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'end', 'minutes', endSecondRef)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                    <span className="time-separator">:</span>
                    <input
                      ref={endSecondRef}
                      type="text"
                      maxLength="2"
                      placeholder="SS"
                      value={endTimeInputs.seconds}
                      onKeyDown={(e) => handleTimeKeyPress(e, 'end', 'seconds', null)}
                      onFocus={handleTimeFocus}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
            <button 
              className="load-history-button"
              onClick={handleLoadHistory}
              disabled={isLoadingHistory}
            >
              {isLoadingHistory ? 'Loading...' : 'Load History'}
            </button>
          </div>
        </div>
      )}

      <div className="video-container">
        {viewMode === 'history' && (
          <div className="motion-events-panel">
            <div className="motion-events-header">
              <h3>Motion Events</h3>
              {isLoadingEvents && <span className="loading-indicator">Loading...</span>}
              {motionEvents.length > 0 && (
                <span className="events-count">{motionEvents.length} events</span>
              )}
            </div>
            
            <div className="motion-events-list">
              {eventsError && (
                <div className="events-error">
                  {eventsError}
                </div>
              )}
              
              {!isLoadingEvents && !eventsError && motionEvents.length === 0 && (
                <div className="no-events">
                  No motion events found for the selected time range.
                </div>
              )}
              
              {motionEvents.map((event, index) => (
                <div 
                  key={index}
                  className="motion-event-item"
                  onClick={() => handleEventClick(event)}
                >
                  <div className="event-time">
                    {formatEventTime(event.start_time)}
                  </div>
                  <div className="event-duration">
                    Duration: {getEventDuration(event.start_time, event.end_time)}
                  </div>
                  <div className="event-timestamp">
                    {new Date(event.start_time).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className={`video-content ${viewMode === 'history' ? 'with-events-panel' : ''}`}>
          <div className="video-wrapper">
            <video
              ref={videoRef}
              controls={viewMode === 'history'}
              autoPlay
              muted
              className={`video-player ${!hasVideoContent && viewMode === 'history' ? 'hidden' : ''}`}
            />
            {viewMode === 'live' && (
              <div className="live-video-controls">
                <button 
                  className="fullscreen-button"
                  onClick={() => {
                    if (videoRef.current) {
                      if (videoRef.current.requestFullscreen) {
                        videoRef.current.requestFullscreen();
                      } else if (videoRef.current.webkitRequestFullscreen) {
                        videoRef.current.webkitRequestFullscreen();
                      } else if (videoRef.current.mozRequestFullScreen) {
                        videoRef.current.mozRequestFullScreen();
                      } else if (videoRef.current.msRequestFullscreen) {
                        videoRef.current.msRequestFullscreen();
                      }
                    }
                  }}
                  title="Fullscreen"
                >
                  ⛶
                </button>
              </div>
            )}
          </div>
          {!hasVideoContent && viewMode === 'history' && (
            <div className="video-placeholder">
              <div className="placeholder-content">
                <h3>History View</h3>
                <p>Select a date/time range and click "Load History" to view recorded footage</p>
              </div>
            </div>
          )}
          {isLoadingLive && viewMode === 'live' && (
            <div className="video-placeholder">
              <div className="placeholder-content">
                <h3>Loading Live Stream...</h3>
                <div className="loading-spinner"></div>
                <p>Connecting to camera feed</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="stream-info">
        <div className="current-chunk">
          <span>{viewMode === 'live' ? 'Live HLS Stream' : 'Historical HLS Stream'} - {camera?.name || cameraId}</span>
          {viewMode === 'history' && startDateTime && endDateTime && (
            <div className="history-range">
              {new Date(startDateTime).toLocaleString()} - {new Date(endDateTime).toLocaleString()}
            </div>
          )}
        </div>
        
        <div className="stats">
          <div>Mode: {viewMode === 'live' ? 'Live' : 'History'}</div>
          <div>Current Time: {hlsStats.currentTime.toFixed(1)}s</div>
          <div>Buffered: {hlsStats.buffered.toFixed(1)}s</div>
          <div>Duration: {hlsStats.duration > 0 ? hlsStats.duration.toFixed(1) + 's' : (viewMode === 'live' ? 'Live' : 'Loading...')}</div>
          <div>Level: {hlsStats.level}</div>
        </div>
      </div>

      <div className="debug-info">
        <strong>Debug:</strong> {debugInfo}
      </div>
    </div>
  );
};

export default CameraViewer; 